import { STATUS_MAP, SUBSTATUS_LABELS } from '../../utils/catalogs';
import { getChartColor } from '../../utils/chartColors';
import { toApiFileUrl } from '../../utils/s3Utils';
import {
  COLLECT_ORDER_TYPES,
  COLLECT_STATUS_LABELS,
  EDITABLE_MIXED_STATUS,
  EDITABLE_ORDER_TYPES,
  ORDER_SOURCE_OPTIONS,
  ORDER_STATUS_LABELS,
  ORDER_TYPE_OPTIONS,
} from './orderConstants';

const labelOf = (options, value, fallback = '') => {
  const option = options.find((item) => item.value === value);
  return option?.label || fallback;
};

export const getOrderTypeLabel = (type) => labelOf(ORDER_TYPE_OPTIONS, type, type || '-');

export const getOrderSourceLabel = (source) =>
  labelOf(ORDER_SOURCE_OPTIONS, source, 'Origen desconocido');

const capitalize = (text) => {
  const value = String(text || '');
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
};

/**
 * Who is physically carrying the parcel.
 *
 * Blue Express pickups and returns never carry a shipping zone, so the courier
 * has to be read off the shipping method instead.
 *
 * @param {object} delivery
 * @returns {string}
 */
export const getOrderProvider = (delivery) => {
  if (!delivery) return '-';
  const { shipping_method: shippingMethod } = delivery;
  if (shippingMethod === 'pick_up_bluex' || shippingMethod === 'devolution_bluex') {
    return 'Bluex';
  }
  return capitalize(delivery.shipping_zone?.courier) || '-';
};

/**
 * Renders an address as one line.
 *
 * Mexican addresses carry a colonia and a postal code on top of the Chilean
 * shape; both are appended when present, because in Mexico an address without
 * them is not deliverable.
 *
 * @param {object} address
 * @returns {string}
 */
export const getFullAddress = (address) => {
  if (!address) return '-';

  const streetLine = [address.street, address.street_number].filter(Boolean).join(' ');
  const parts = [
    streetLine,
    address.settlement,
    address.commune,
    address.region,
    address.postal_code ? `CP ${address.postal_code}` : '',
  ].filter(Boolean);

  return parts.join(', ') || '-';
};

/**
 * Firebase-hosted proof-of-delivery images are stored by some couriers without
 * the `alt=media` query that makes Firebase return the bytes instead of the
 * object metadata. Without it the `<img>` renders broken.
 *
 * Images uploaded from the status modal come back relative to the API in
 * development (`/pod_images/…`), and are resolved against it.
 */
const normalizeImageUrl = (url) => {
  if (!url.includes('firebasestorage.googleapis.com') || url.includes('alt=media')) {
    return toApiFileUrl(url);
  }
  return `${url}${url.includes('?') ? '&' : '?'}alt=media`;
};

/** Delivery statuses only — none of the order's own. */
export const DELIVERY_STATUS_LABELS = Object.entries(STATUS_MAP).reduce(
  (acc, [systemName, status]) => {
    acc[systemName] = status.user_name;
    return acc;
  },
  {}
);

/**
 * The statuses an order of this type can be in, and what each one is called.
 *
 * Collect orders (Click & Collect, Pay & Collect) run on order-level statuses;
 * everything else runs on delivery statuses plus the few order-level ones that
 * precede a dispatch. A status missing from the result is one this kind of
 * order has no business being in.
 *
 * @param {string} orderType
 * @returns {Object<string, string>} System name → the name a person reads.
 */
export const statusLabelsFor = (orderType) => {
  if (COLLECT_ORDER_TYPES.includes(orderType)) return COLLECT_STATUS_LABELS;

  return { ...ORDER_STATUS_LABELS, ...DELIVERY_STATUS_LABELS };
};

/**
 * Turns raw status events into timeline entries.
 *
 * An event whose status has no label in `statusLabels` is dropped, which is how
 * unrelated internal transitions stay out of a history a person reads.
 *
 * @param {Array<object>} history - Events as the backend sends them, oldest first.
 * @param {Object<string, string>} statusLabels - System name → the name a person reads.
 * @returns {Array<{id: number, status: string, statusLabel: string, substatus: string,
 *   substatusLabel: string, date: string, details: string, imageUrls: string[],
 *   source: string, externalSource: string, color: string}>}
 */
export const toTimelineEvents = (history, statusLabels) => {
  if (!Array.isArray(history) || history.length === 0) return [];

  return history
    .filter((event) => Boolean(statusLabels[event.status]))
    .map((event) => ({
      id: event.id,
      status: event.status,
      statusLabel: statusLabels[event.status],
      substatus: event.substatus,
      substatusLabel: SUBSTATUS_LABELS[event.substatus] || '',
      date: event.date,
      details: event.details || '',
      imageUrls: (event.image_urls || []).map(normalizeImageUrl),
      source: event.source,
      externalSource: event.external_source,
      // The same palette the badges and the chips use. The catalog's gives
      // eight statuses one grey, so a shipment that passed through five of them
      // drew five identical dots.
      color: getChartColor(event.status),
    }));
};

/**
 * The status timeline of an order as the internal team reads it: the order's
 * own events and its shipment's, merged.
 *
 * Public tracking does not use this — it shows the shipment's history alone.
 *
 * @param {object} order
 */
export const buildStatusTimeline = (order) =>
  toTimelineEvents(order?.combined_status_history, statusLabelsFor(order?.type));

/**
 * Whether the shipping data of the order can still be edited.
 *
 * Once the order leaves preparation a courier already holds the parcel, so
 * changing the address or the warehouse would only desynchronise us from them.
 *
 * @param {object} order
 * @returns {boolean}
 */
export const canEditOrderInfo = (order) =>
  order?.mixed_status === EDITABLE_MIXED_STATUS &&
  EDITABLE_ORDER_TYPES.includes(order?.type);

/**
 * Formats a date the way the platform has always shown it: `dd/mm/yyyy - hh:mm`.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const pad = (number) => String(number).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} - ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};
